import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { WidgetTheme } from '@intent/shared';
import type { ChatClient, ChatState } from '../core/chat-client';
import { Avatar } from './Avatar';
import { PresenceLine } from './PresenceLine';

export function ChatPanel({
  client,
  state,
  theme,
  founderName,
  onClose,
}: {
  client: ChatClient;
  state: ChatState;
  theme: WidgetTheme;
  founderName: string;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Pin to the newest message. Layout effect so it happens before paint and
  // the list never visibly jumps.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [state.messages.length, state.isAgentTyping]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function send() {
    const content = draft.trim();
    if (!content || sending) return;

    setSending(true);
    setDraft('');

    try {
      await client.sendMessage(content);
    } catch {
      // Restore the draft so a failed send does not lose what was typed.
      setDraft(content);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter is a newline — the convention every chat uses.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  }

  return (
    <div className="panel" role="dialog" aria-label="Chat">
      <div className="header">
        <Avatar name={founderName} src={theme.founderAvatarUrl || undefined} size={36} />
        <div>
          <div className="header-title">{founderName}</div>
          <PresenceLine role={theme.founderRole} availabilityState={state.availabilityState} />
        </div>
        <button className="header-close" onClick={onClose} aria-label="Close chat">
          ×
        </button>
      </div>

      <div className="messages" ref={listRef} aria-live="polite">
        {state.messages.length === 0 && !state.isAgentTyping && (
          <p className="empty">{state.greeting}</p>
        )}

        {state.messages.map((message) => {
          const fromFounderSide = message.role !== 'VISITOR';
          return (
            <div key={message.id} className="message-row" data-role={message.role}>
              {fromFounderSide && (
                <Avatar name={founderName} src={theme.founderAvatarUrl || undefined} size={24} />
              )}
              <div className="message" data-role={message.role}>
                {message.content}
              </div>
            </div>
          );
        })}

        {state.isAgentTyping && (
          <div className="message-row" data-role="AI">
            <Avatar name={founderName} src={theme.founderAvatarUrl || undefined} size={24} />
            <div className="typing" aria-label="Typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>

      <div className="composer">
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          placeholder="Write a message…"
          onChange={(event) => {
            setDraft(event.target.value);
            client.setTyping(event.target.value.length > 0);
          }}
          onKeyDown={handleKeyDown}
          aria-label="Message"
        />
        <button onClick={() => void send()} disabled={!draft.trim() || sending}>
          Send
        </button>
      </div>
    </div>
  );
}
